import {
  type LazyQueryHookOptions,
  type MutationHookOptions,
  type QueryHookOptions,
  useLazyQuery,
  useMutation,
  useQuery,
} from "@apollo/client";
import {
  AutomationEditorQuery,
  AutomationEnrollmentsQuery,
  AutomationNodeActiveCountQuery,
  AutomationFlowsListQuery,
  AutomationGraphIssuesQuery,
  AutomationGraphRulesQuery,
  AutomationLiveQuery,
} from "../graphql/queries";
import {
  ActivateAutomationMutation,
  CreateAutomationMutation,
  PauseAutomationMutation,
  UpdateAutomationMutation,
} from "../graphql/mutations";
import type {
  ActivateAutomationMutationType,
  ActivateAutomationMutationVariablesType,
  AutomationEditorQueryType,
  AutomationEditorQueryVariablesType,
  AutomationEnrollmentsQueryType,
  AutomationEnrollmentsQueryVariablesType,
  AutomationFlowsListQueryType,
  AutomationFlowsListQueryVariablesType,
  AutomationGraphIssuesQueryType,
  AutomationGraphIssuesQueryVariablesType,
  AutomationGraphRulesQueryType,
  AutomationGraphRulesQueryVariablesType,
  AutomationLiveQueryType,
  AutomationLiveQueryVariablesType,
  AutomationNodeActiveCountQueryType,
  AutomationNodeActiveCountQueryVariablesType,
  CreateAutomationMutationType,
  CreateAutomationMutationVariablesType,
  PauseAutomationMutationType,
  PauseAutomationMutationVariablesType,
  UpdateAutomationMutationType,
  UpdateAutomationMutationVariablesType,
} from "@/types/graphql/partner.generated";

export const useAutomationFlowsListQuery = (
  options?: QueryHookOptions<
    AutomationFlowsListQueryType,
    AutomationFlowsListQueryVariablesType
  >
) =>
  useQuery<AutomationFlowsListQueryType, AutomationFlowsListQueryVariablesType>(
    AutomationFlowsListQuery,
    options
  );

export const useAutomationEditorQuery = (
  options?: QueryHookOptions<
    AutomationEditorQueryType,
    AutomationEditorQueryVariablesType
  >
) =>
  useQuery<AutomationEditorQueryType, AutomationEditorQueryVariablesType>(
    AutomationEditorQuery,
    options
  );

// Опрос открытого редактора (супер-ревью 4): только статус, версия,
// действия и счётчики. Ответ обновляет Automation:<id> в кеше, редактор
// читает их оттуда через AutomationEditorQuery.
export const useAutomationLiveQuery = (
  options?: QueryHookOptions<
    AutomationLiveQueryType,
    AutomationLiveQueryVariablesType
  >
) =>
  useQuery<AutomationLiveQueryType, AutomationLiveQueryVariablesType>(
    AutomationLiveQuery,
    options
  );

// Ошибки несохранённого графа: редактор зовёт с задержкой после правок.
export const useAutomationGraphIssuesQuery = (
  options?: QueryHookOptions<
    AutomationGraphIssuesQueryType,
    AutomationGraphIssuesQueryVariablesType
  >
) =>
  useQuery<
    AutomationGraphIssuesQueryType,
    AutomationGraphIssuesQueryVariablesType
  >(AutomationGraphIssuesQuery, options);

// Шаблоны, с которых можно начать сценарий вида (диалог создания): меняются
// только с выкатом. Правила графа редактор берёт из Automation.graphRules.
export const useAutomationGraphRulesQuery = (
  options?: QueryHookOptions<
    AutomationGraphRulesQueryType,
    AutomationGraphRulesQueryVariablesType
  >
) =>
  useQuery<
    AutomationGraphRulesQueryType,
    AutomationGraphRulesQueryVariablesType
  >(AutomationGraphRulesQuery, { fetchPolicy: "cache-first", ...options });

// Участники цепочки (вкладка участников и шаг на полотне): курсорная
// страница, новые сверху. Список меняется постоянно: по умолчанию кеш
// показывается сразу и тут же перечитывается из сети.
export const useAutomationEnrollmentsQuery = (
  options?: QueryHookOptions<
    AutomationEnrollmentsQueryType,
    AutomationEnrollmentsQueryVariablesType
  >
) =>
  useQuery<
    AutomationEnrollmentsQueryType,
    AutomationEnrollmentsQueryVariablesType
  >(AutomationEnrollmentsQuery, {
    fetchPolicy: "cache-and-network",
    ...options,
  });

// Сколько зрителей стоит на шаге: спрашивается по требованию перед
// удалением шага, поэтому ленивый запрос без кеша.
export const useAutomationNodeActiveCountLazyQuery = (
  options?: LazyQueryHookOptions<
    AutomationNodeActiveCountQueryType,
    AutomationNodeActiveCountQueryVariablesType
  >
) =>
  useLazyQuery<
    AutomationNodeActiveCountQueryType,
    AutomationNodeActiveCountQueryVariablesType
  >(AutomationNodeActiveCountQuery, {
    fetchPolicy: "network-only",
    ...options,
  });

/**
 * Перечитывание списка автоматизаций (супер-ревью 2). Перечитывается он
 * только там, где меняется состав страницы: создание (новая строка), архив
 * и удаление (useAutomations.ts). Сохранение, запуск и пауза возвращают
 * автоматизацию фрагментом редактора: ответ обновляет `Automation:<id>` в
 * кеше, а с ним строку списка (статус, счётчики, событие триггера). Выпасть
 * из отфильтрованного по статусу списка строка может только после запуска
 * или паузы, такой список перечитывается один раз при закрытии диалога
 * (Automations.tsx, listRefetchOnClose).
 */
export const AUTOMATION_LIST_REFETCH = {
  create: ["AutomationFlowsList"],
  update: [],
  activate: [],
  pause: [],
} as const satisfies Record<string, readonly string[]>;

const refetchOf = (queries: readonly string[]) =>
  queries.length > 0 ? { refetchQueries: [...queries] } : {};

export const useCreateAutomationMutation = (
  options?: MutationHookOptions<
    CreateAutomationMutationType,
    CreateAutomationMutationVariablesType
  >
) =>
  useMutation<
    CreateAutomationMutationType,
    CreateAutomationMutationVariablesType
  >(CreateAutomationMutation, {
    ...refetchOf(AUTOMATION_LIST_REFETCH.create),
    ...options,
  });

export const useUpdateAutomationMutation = (
  options?: MutationHookOptions<
    UpdateAutomationMutationType,
    UpdateAutomationMutationVariablesType
  >
) =>
  useMutation<
    UpdateAutomationMutationType,
    UpdateAutomationMutationVariablesType
  >(UpdateAutomationMutation, {
    ...refetchOf(AUTOMATION_LIST_REFETCH.update),
    ...options,
  });

export const useActivateAutomationMutation = (
  options?: MutationHookOptions<
    ActivateAutomationMutationType,
    ActivateAutomationMutationVariablesType
  >
) =>
  useMutation<
    ActivateAutomationMutationType,
    ActivateAutomationMutationVariablesType
  >(ActivateAutomationMutation, {
    ...refetchOf(AUTOMATION_LIST_REFETCH.activate),
    ...options,
  });

export const usePauseAutomationMutation = (
  options?: MutationHookOptions<
    PauseAutomationMutationType,
    PauseAutomationMutationVariablesType
  >
) =>
  useMutation<
    PauseAutomationMutationType,
    PauseAutomationMutationVariablesType
  >(PauseAutomationMutation, {
    ...refetchOf(AUTOMATION_LIST_REFETCH.pause),
    ...options,
  });
